import { api } from './client';
import type { EnvelopeDocument } from './envelopes.api';

type Envelope<T> = { data: T };

export type UploadedDocument = EnvelopeDocument & {
  envelopeId: string;
  mimeType: string;
  hashSha256: string;
  createdAt: string;
};

export const documentsApi = {
  async upload(envelopeId: string, file: File, onProgress?: (pct: number) => void): Promise<UploadedDocument> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<Envelope<UploadedDocument>>(
      `/documents/upload?envelopeId=${envelopeId}`,
      form,
      {
        // 2-minute ceiling: Office docs go through LibreOffice headless on
        // the server, which can need 10-30s for the first conversion in a
        // cold container (font cache warm-up + JVM start). The 15s default
        // on the shared client kills the request mid-conversion.
        timeout: 120_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
    return data.data;
  },

  async createFromHtml(
    envelopeId: string,
    payload: { title: string; html: string },
  ): Promise<UploadedDocument> {
    const { data } = await api.post<Envelope<UploadedDocument>>(
      `/documents/from-html?envelopeId=${envelopeId}`,
      payload,
      // HTML→PDF render is normally fast, but large documents with many
      // pages can take longer than the global 15s — bump for safety.
      { timeout: 60_000 },
    );
    return data.data;
  },

  async getHtml(uuid: string): Promise<{ html: string; title: string }> {
    const { data } = await api.get<Envelope<{ html: string; title: string }>>(
      `/documents/${uuid}/html`,
    );
    return data.data;
  },

  async updateHtml(
    uuid: string,
    payload: { title?: string; html: string },
  ): Promise<UploadedDocument> {
    const { data } = await api.patch<Envelope<UploadedDocument>>(
      `/documents/${uuid}/html`,
      payload,
    );
    return data.data;
  },

  async replaceFile(
    uuid: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadedDocument & { pageMismatch: boolean }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<Envelope<UploadedDocument & { pageMismatch: boolean }>>(
      `/documents/${uuid}/replace`,
      form,
      {
        timeout: 120_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
    return data.data;
  },

  /**
   * Replace a document's PDF in place regardless of envelope status (Existing
   * Envelopes screen). Sender/admin only; any prior signed render is dropped.
   */
  async replaceFileForce(
    uuid: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadedDocument & { pageMismatch: boolean }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<Envelope<UploadedDocument & { pageMismatch: boolean }>>(
      `/documents/${uuid}/replace-force`,
      form,
      {
        timeout: 120_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
    return data.data;
  },

  /**
   * Returns a Blob URL for the PDF that can be passed to react-pdf or an iframe.
   * Caller is responsible for revoking the URL on unmount.
   */
  async fetchAsBlobUrl(uuid: string): Promise<string> {
    const res = await api.get<Blob>(`/documents/${uuid}/file`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },

  /** Same as fetchAsBlobUrl but returns the flattened/signed PDF. */
  async fetchSignedAsBlobUrl(uuid: string): Promise<string> {
    const res = await api.get<Blob>(`/documents/${uuid}/signed`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },

  async delete(uuid: string): Promise<void> {
    await api.delete(`/documents/${uuid}`);
  },

  /** Force-download the signed (flattened) PDF. Falls back to original if not yet signed. */
  async downloadSigned(uuid: string, filename: string): Promise<void> {
    const res = await api.get<Blob>(`/documents/${uuid}/signed`, { responseType: 'blob' });
    triggerBlobDownload(res.data, filename);
  },
};

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
